"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { X, Check, Filter } from "lucide-react";
import TychoButton from "src/components/atoms/buttons/TychoButton";
import SectionHeading from "src/components/molecules/SectionHeading";
import { caseStudies as mockCaseStudies } from "src/utils/mock/caseStudies";
import StoriesHero from "src/components/molecules/SuccessStoriesSections/StoriesHero";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const CATEGORIES = [
  "Govt.",
  "E-commerce",
  "Social Media",
  "Entertainment",
  "3D Scanning & Reconstruction",
  "Education",
  "Health & Fitness",
  "Communication",
  "Travel & Navigation",
  "Business",
  "News & Media",
];

export default function SuccessStoriesComponent({ caseStudies: apiCaseStudies }) {
  const caseStudies = apiCaseStudies || mockCaseStudies;
  const [visibleOtherCount, setVisibleOtherCount] = useState(8);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedCategories, visibleOtherCount]);

  const liveProjects = (caseStudies?.projects?.liveProjects || []).map((p) => ({ ...p, isUpcoming: false }));
  const upcomingProjects = (caseStudies?.projects?.upcomingProjects || []).map((p) => ({ ...p, isUpcoming: true }));
  const allProjects = [...liveProjects, ...upcomingProjects];

  const filteredProjects = selectedCategories.length > 0 ? allProjects.filter((p) => p.category && selectedCategories.includes(p.category.trim())) : allProjects;

  const topProjects = filteredProjects.slice(0, 15);
  const otherProjects = filteredProjects.slice(15);
  const displayedOtherProjects = otherProjects.slice(0, visibleOtherCount);

  const renderProjectCard = (study, displayIndex) => {
    const formattedIndex = String(displayIndex).padStart(2, "0");
    if (!study.isUpcoming) {
      return (
        <div
          key={formattedIndex}
          className="group relative rounded-2xl border border-border-primary bg-surface-primary overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-violet-500/50 shadow-[0_0_25px_rgba(76,29,149,0.15)] flex flex-col h-full"
        >
          <div className="relative h-[240px] overflow-hidden border-b border-border-secondary">
            <div className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold shadow-lg">
              {formattedIndex}
            </div>

            <Image src={study.bannerImage} alt={study.title} fill />

            <div className="absolute inset-0 bg-gradient-to-t from-surface-primary via-black/10 to-transparent hidden dark:block" />
          </div>

          <div className="p-5! flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-text-primary text-xl font-bold">{study.title}</h3>
              <span className="text-text-primary text-sm font-semibold">{study.year}</span>
            </div>

            <div className="flex flex-col items-start gap-2 mt-2.5! h-[80px] overflow-hidden">
              {study?.category && (
                <span className="bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[10px] font-bold uppercase tracking-wider px-2.5! py-1! rounded-md border border-violet-600/20 dark:border-violet-500/20">
                  {study.category}
                </span>
              )}
              {study.subCategory && (
                <span className="bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 text-[10px] font-bold uppercase tracking-wider px-2.5! py-1! rounded-md border border-fuchsia-600/20 dark:border-fuchsia-500/20">
                  {study.subCategory}
                </span>
              )}
            </div>

            <div className="mt-3!">
              <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-sm font-semibold uppercase tracking-wide mb-2!">
                Project Description
              </h4>
              <p className="text-text-secondary text-sm leading-5 line-clamp-3 h-[60px] overflow-hidden">{study.description}</p>
            </div>

            <div className="flex flex-row items-end justify-between mt-auto pt-4! gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-sm font-semibold uppercase tracking-wide mb-2!">
                  Technology
                </h4>
                <div className="flex flex-wrap gap-2.5!">
                  {study.technologies?.slice(0, 5).map((tech, idx) => (
                    <div key={idx} className="flex flex-col items-center hover:border-violet-500 transition-all">
                      <Image src={tech.icon} alt={tech.name} width={22} height={22} className="object-contain" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <TychoButton size="sm" variant="secondary" href={study.websiteUrl} title="Explore More" target="_blank" titleSize="16px" className="px-2! text-sm! py-2!" />
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          key={formattedIndex}
          className="group relative rounded-2xl border border-border-primary bg-surface-primary overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-fuchsia-500/50 shadow-[0_0_25px_rgba(217,70,239,0.15)] flex flex-col h-full"
        >
          <div className="relative h-[240px] overflow-hidden border-b border-border-secondary">
            <div className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-600 to-violet-500 flex items-center justify-center text-white text-xs font-bold shadow-lg">
              {formattedIndex}
            </div>

            <Image src={study.bannerImage} alt={study.title} fill />
            <div className="absolute inset-0 bg-gradient-to-t from-surface-primary via-black/20 to-transparent hidden dark:block" />
          </div>

          <div className="p-5! flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-text-primary text-xl font-bold">{study.title}</h3>
              <span className="text-text-primary text-sm font-semibold">{study.year}</span>
            </div>

            <div className="flex flex-col items-start gap-2 mt-2.5! h-[80px] overflow-hidden">
              {study?.category && (
                <span className="bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[10px] font-bold uppercase tracking-wider px-2.5! py-1! rounded-md border border-violet-600/20 dark:border-violet-500/20">
                  {study.category}
                </span>
              )}
              {study?.subCategory && (
                <span className="bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 text-[10px] font-bold uppercase tracking-wider px-2.5! py-1! rounded-md border border-fuchsia-600/20 dark:border-fuchsia-500/20">
                  {study.subCategory}
                </span>
              )}
            </div>

            <div className="mt-3!">
              <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-sm font-semibold uppercase tracking-wide mb-2!">
                Project Description
              </h4>
              <p className="text-text-secondary text-sm leading-5 line-clamp-3 h-[60px] overflow-hidden">{study.description}</p>
            </div>

            <div className="flex flex-row items-end justify-between mt-auto pt-4! gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-sm font-semibold uppercase tracking-wide mb-2!">
                  Technology
                </h4>
                <div className="flex flex-wrap gap-2.5!">
                  {study.technologies?.slice(0, 5).map((tech, idx) => (
                    <div key={idx} className="flex flex-col items-center hover:border-violet-500 transition-all">
                      <Image src={tech.icon} alt={tech.name} width={22} height={22} className="object-contain" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <TychoButton
                  size="sm"
                  variant="secondary"
                  href={`/success-stories/${study.slug || study.id}`}
                  title="View Details"
                  titleSize="16px"
                  className="px-2! text-sm! py-2!"
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="bg-background-primary text-ink overflow-hidden">
      <StoriesHero />
      <section className="relative bg-background-primary overflow-hidden pt-10! pb-12!">
        <div className="container relative z-10">
          <SectionHeading maxWidth={1100} miniTitle="Success" miniHighlight="Stories" />

          <div className="mb-16! ">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8! gap-4">
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-2 mb-2!">
                  <span className="h-[2px] w-8 bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-full" />
                  <span className="text-xs font-bold uppercase tracking-widest text-violet-500 dark:text-violet-400">Featured Portfolio</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-display font-extrabold text-text-primary">{selectedCategories.length > 0 ? "Filter result" : "Our Top Clients"}</h3>
              </div>

              <button
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2 px-5! py-2.5! rounded-full bg-surface-secondary border border-border-primary hover:border-violet-500 text-text-primary font-medium transition-all shadow-sm"
              >
                <Filter size={18} />
                Filters
                {selectedCategories.length > 0 && <span className="bg-violet-600 text-white text-[10px] font-bold px-2! py-0.5! rounded-full">{selectedCategories.length}</span>}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6!">
              {topProjects.map((study, index) => renderProjectCard(study, index + 1))}
            </div>
          </div>

          {otherProjects.length > 0 && (
            <div className="mt-20! md:mt-10!">
              <div className="flex flex-col items-start mb-8!">
                <div className="flex items-center gap-2 mb-2!">
                  <span className="h-[2px] w-8 bg-gradient-to-r from-fuchsia-500 to-cyan-500 rounded-full" />
                  <span className="text-xs font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">Extended Work</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-display font-extrabold text-text-primary">Other Projects</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6!">
                {displayedOtherProjects.map((study, index) => renderProjectCard(study, index + 16))}
              </div>

              {otherProjects.length > visibleOtherCount && (
                <div className="flex justify-center mt-12!">
                  <TychoButton size="md" variant="primary" onClick={() => setVisibleOtherCount((prev) => prev + 8)} className="p-4!" title="Load More" />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Filter Drawer */}
      <div className={`fixed inset-0 z-50 transition-opacity duration-300 ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsFilterOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-md bg-white dark:bg-[#0f172a] shadow-2xl transition-transform duration-300 transform ${isFilterOpen ? "translate-x-0" : "translate-x-full"} flex flex-col`}
        >
          <div className="bg-[#592ea9] flex items-center p-4">
            <button onClick={() => setIsFilterOpen(false)} className="text-white p-2! mr-2! hover:bg-white/20 rounded-full transition">
              <X size={24} />
            </button>
            <h2 className="text-white text-xl! font-medium">Filter results</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-6!">
            <div className="flex justify-between items-center my-4!">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white m-0!">Choose category</h3>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded-full transition"
                aria-label="Close filters"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              {CATEGORIES.map((category) => {
                const isSelected = selectedCategories.includes(category);
                return (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategories((prev) => (isSelected ? prev.filter((c) => c !== category) : [...prev, category]));
                    }}
                    className={`flex items-center gap-2 px-4! py-2! rounded-full border text-sm font-medium transition-all cursor-pointer ${
                      isSelected
                        ? "border-gray-400 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white"
                        : "border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1e293b] text-gray-600 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500"
                    }`}
                  >
                    {isSelected && <Check size={16} className="text-gray-700 dark:text-gray-200" />}
                    {category}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-4! border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#1e293b] flex justify-between items-center">
            {/* <button
              onClick={() => setSelectedCategories([])}
              className="text-[#592ea9] dark:text-violet-400 font-medium px-4! py-2! hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-lg transition"
            >
              Clear all
            </button> */}
            <TychoButton size="md" variant="secondary" onClick={() => setSelectedCategories([])} className="px-6! py-1!" title="Clear all" />
            <TychoButton size="md" variant="primary" onClick={() => setIsFilterOpen(false)} title={`Show results (${filteredProjects.length})`} className="px-6! py-1!" />
          </div>
        </div>
      </div>
    </div>
  );
}
